import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import WaitlistForm from '../components/WaitlistForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const { FiCheck, FiMail, FiHelpCircle, FiShoppingCart } = FiIcons;

const PricingPage = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "NinjaCopia Lifetime License",
    "image": "https://ninjacopia.com/og-image.jpg",
    "description": "Lifetime access to NinjaCopia professional trade copier.",
    "brand": {
      "@type": "Brand",
      "name": "NinjaCopia"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://ninjacopia.com/#/pricing",
      "priceCurrency": "USD",
      "price": "59.00",
      "priceValidUntil": "2025-12-31",
      "availability": "https://schema.org/PreOrder"
    }
  };

  const { user } = useAuth();
  const navigate = useNavigate();

  const handlePurchase = (plan) => {
    if (!user) {
      // Redirect to signup if not logged in
      navigate('/signup');
      return;
    }

    // STRIPE INTEGRATION POINT
    // 1. Call your backend/edge function to create a Stripe Checkout Session
    // 2. Pass the plan.stripeId
    // 3. Redirect user to the Stripe URL returned
    console.log('Initiating purchase for:', plan.name);
    alert(`Checkout for ${plan.name} coming soon! Join the waitlist for now.`);
  };

  const plans = [
    {
      name: 'Single Machine License',
      originalPrice: 147,
      currentPrice: 97,
      savings: 50,
      stripeId: 'price_single_machine_id', // Placeholder ID
      description: 'Perfect for individual traders',
      features: [
        '1 machine activation',
        '1 main account, unlimited followers',
        'Priority Email support',
        'Advanced risk management',
        'Lifetime license for purchased version',
        '30-day money-back guarantee',
        'Free updates for 1 year',
        'Multi-language UI (EN/ES/FR)'
      ],
      popular: false
    },
    {
      name: 'Two Machine License',
      originalPrice: 250,
      currentPrice: 147,
      savings: 103,
      stripeId: 'price_two_machine_id', // Placeholder ID
      description: 'Great for small trading teams',
      features: [
        '2 machine activations',
        '1 main account, unlimited followers',
        'Priority Email support',
        'Advanced risk management',
        'Lifetime license for purchased version',
        '30-day money-back guarantee',
        'Free updates for 1 year',
        'Multi-language UI (EN/ES/FR)'
      ],
      popular: true
    },
    {
      name: 'Three Machine License',
      originalPrice: 350,
      currentPrice: 197,
      savings: 153,
      stripeId: 'price_three_machine_id', // Placeholder ID
      description: 'Ideal for trading firms',
      features: [
        '3 machine activations',
        '1 main account, unlimited followers',
        'Priority Email support',
        'Advanced risk management',
        'Lifetime license for purchased version',
        '30-day money-back guarantee',
        'Free updates for 1 year',
        'Multi-language UI (EN/ES/FR)'
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: 'What happens after 1 year of free updates?',
      answer: 'You keep your licensed version forever. Major version updates (NinjaCopia 2.0) may require an upgrade fee.'
    },
    {
      question: 'Can I transfer my license to a new computer?',
      answer: 'Yes, with license deactivation/reactivation through our self-service portal.'
    },
    {
      question: 'Is there a trial version available?',
      answer: 'We offer a 30-day money-back guarantee instead of a trial. This ensures you get the full experience with your actual trading setup.'
    },
    {
      question: 'Can I upgrade my license later?',
      answer: 'Yes, you can add additional machine activations at prorated pricing anytime.'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title="Pricing & Lifetime License"
        description="One simple price for lifetime access. Get the NinjaCopia multi-account trade copier for just $59 one-time payment."
        keywords="NinjaTrader copier price, NinjaCopia cost, trade copier lifetime license"
        url="https://ninjacopia.com/#/pricing"
        schema={schema}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900/20 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Simple, Transparent Pricing
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 dark:text-gray-400 mb-8"
            >
              All plans include: Lifetime license for the purchased version • 30-day money-back guarantee • Free updates for 1 year • Multi-language UI (EN/ES/FR)
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-sm font-medium"
            >
              Limited Time: Founder's Pricing - Save up to 50%
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm border-2 transition-all duration-200 hover:shadow-lg ${plan.popular
                  ? 'border-blue-500 dark:border-blue-400'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                  }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {plan.description}
                  </p>

                  <div className="flex items-baseline mb-6">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">
                      ${plan.currentPrice}
                    </span>
                    <span className="text-lg text-gray-500 dark:text-gray-400 line-through ml-2">
                      ${plan.originalPrice}
                    </span>
                    <span className="text-sm text-green-600 dark:text-green-400 ml-2 font-medium">
                      (save ${plan.savings})
                    </span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <SafeIcon
                          icon={FiCheck}
                          className="w-5 h-5 text-green-500 mr-3 mt-0.5 shrink-0"
                        />
                        <span className="text-gray-600 dark:text-gray-400 text-sm">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Purchase Button - Toggle comment to enable Stripe purchase flow
                  <button
                    onClick={() => handlePurchase(plan)}
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center ${
                      plan.popular
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
                    }`}
                  >
                    <SafeIcon icon={FiShoppingCart} className="w-5 h-5 mr-2" />
                    Buy Now
                  </button>
                  */}

                  {/* Waitlist Button (Current Default) */}
                  <button
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${plan.popular
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
                      }`}
                  >
                    Join Waitlist
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
              <em>Note: Pricing is per license key, tied to machine ID. Additional machines can be added later at prorated pricing.</em>
            </p>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-8 max-w-2xl mx-auto">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Join Waitlist to Lock in Founder's Pricing
              </h3>
              <WaitlistForm buttonText="Secure My Spot" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start">
                  <SafeIcon
                    icon={FiHelpCircle}
                    className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-4 mt-0.5 shrink-0"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Q: {faq.question}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      A: {faq.answer}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;